import type { Locale as CalendarNames } from 'react-day-picker'
import { de, enCA, enGB, enUS, es, fr, frCA, it, nl } from 'react-day-picker/locale'

import { localeFormatter } from '@/i18n/format'
import { defaultLocale, type Locale } from '@/i18n/locales'

export interface CalendarLocale {
    /**
     * The calendar's month/weekday names **and** its accessible labels.
     *
     * `react-day-picker/locale` re-exports each date-fns locale with a `labels` bucket
     * added (`labelNext`, `labelPrevious`, `labelDayButton`, `labelGrid`, …), and
     * `getLabels` prefers `locale.labels` over its English defaults — so importing from
     * here rather than `date-fns/locale` is what keeps the calendar's accessible names
     * localized. The date-fns object alone carries no `labels`, leaving a
     * screen reader on an es-mx page hearing "Go to the Next Month" under a Spanish
     * caption.
     */
    locale: CalendarNames
    /** 0 = Sunday … 6 = Saturday. */
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6
}

/**
 * **`es-mx` maps to `es` because `es` is the only Spanish bundle that exists.** The
 * `locale` value is a *bundle* — name tables plus label functions — not a locale tag,
 * which is why the keys and values here are spelled differently. `date-fns` ships exactly
 * one Spanish one (`code: 'es'`, Spain); there is no `esMX`, so a Mexican calendar
 * borrows a Spanish-from-Spain one. `enUS` is genuinely regional by contrast, and that
 * asymmetry is the whole tell: en-us gets its own bundle, es-mx has to borrow.
 *
 * The borrow is safe for the names themselves — `enero` and `lunes` are the same either
 * side of the Atlantic — and Spanish's regional divergence falls in date *ordering*,
 * which no caller takes from this bundle (the trigger button formats through
 * `formatShortDate`, the caption through `formatCalendarCaption` below).
 *
 * **The week start is the one field where the borrow is regionally wrong**, hence pinned
 * rather than read off it: `es.options.weekStartsOn` is Monday, where CLDR gives Mexico
 * Sunday-first like the US. `Intl`'s week data *can* supply it, and agrees with CLDR —
 * it is out on availability, not accuracy: browser support for the accessor is
 * incomplete, and a client-rendered calendar would need this table as its fallback
 * anyway.
 *
 * **`code` is restated as the served tag** so the borrow does not leak one. A bundle
 * carries the tag it was shipped under (`es`), and that is the only locale *tag* the
 * calendar would otherwise hold — every other locale-driven string in this control goes
 * through `localeFormatter(locale)` on the page's own `es-mx`. Restating it keeps the
 * calendar from being the one place naming a different locale than everything beside it,
 * and makes the composed value honest: with the week start pinned to Mexico's, the
 * object *is* es-MX, and its name tables are byte-identical to it (`Intl` gives `es`,
 * `es-MX` and `es-ES` the same weekday and month names — the Spanish regional split is
 * in date ordering and the first day of the week, neither taken from here).
 */
const CALENDAR_LOCALES: Record<Locale, CalendarLocale> = {
    'en-us': { locale: enUS, weekStartsOn: 0 },
    'es-mx': { locale: { ...es, code: 'es-MX' }, weekStartsOn: 0 },
    // `enCA` is a genuine regional bundle, so nothing is borrowed and its own
    // Sunday-first week start is restated here rather than read off it, for the same
    // reason the rows above pin theirs: one table answers the question for every locale.
    'en-ca': { locale: enCA, weekStartsOn: 0 },
    // Also a genuine regional bundle — and the one to take rather than `fr`, which is
    // France's and Monday-first. Canada is Sunday-first in either language.
    'fr-ca': { locale: frCA, weekStartsOn: 0 },
    // Italian ships only as `it` (Italy's) and `it-CH`, so it-it borrows `it` and
    // restates the tag as es-mx does. Italy is Monday-first in CLDR, unlike the American
    // rows above, which is why no row here may copy another's week start.
    'it-it': { locale: { ...it, code: 'it-IT' }, weekStartsOn: 1 },
    // `nl` is the Netherlands' bundle — Belgium's ships separately as `nlBE` — but under
    // the bare language tag, so `code` is restated as es-mx's is. The Netherlands is
    // Monday-first in CLDR: the pin follows the market, not the other served locales.
    'nl-nl': { locale: { ...nl, code: 'nl-NL' }, weekStartsOn: 1 },
    // Borrowed like es-mx's, with `code` restated as the served tag for the same reason.
    // Germany is Monday-first in CLDR; pinned rather than read off the bundle.
    'de-de': { locale: { ...de, code: 'de-DE' }, weekStartsOn: 1 },
    // A genuine regional bundle, like `enCA`. The United Kingdom is Monday-first in CLDR,
    // where both North American English rows are Sunday-first.
    'en-gb': { locale: enGB, weekStartsOn: 1 },
    // `es` is Spain's own bundle, shipped under the bare language tag, so `code` is restated
    // as es-mx's is. Spain is Monday-first where Mexico is Sunday-first: the two rows
    // share a bundle and differ in the week start.
    'es-es': { locale: { ...es, code: 'es-ES' }, weekStartsOn: 1 },
    // `fr` is France's own bundle, shipped under the bare language tag, so `code` is
    // restated. France is Monday-first where fr-ca's row above is Sunday-first: the
    // two French rows share a language and nothing else here.
    'fr-fr': { locale: { ...fr, code: 'fr-FR' }, weekStartsOn: 1 },
}

/**
 * Calendar conventions for the active locale — total over whatever tag reaches it, falling
 * back to the default locale the way `localeFormatter` does.
 *
 * Case-folded on the same grounds as the shared formatter: BCP-47 is case-insensitive, so
 * without the fold a conventionally-cased `es-MX` resolves here to English while the
 * trigger button beside it formats in Spanish — the two halves of one control disagreeing.
 */
export function calendarLocale(locale: string): CalendarLocale {
    return CALENDAR_LOCALES[locale.toLowerCase() as Locale] ?? CALENDAR_LOCALES[defaultLocale]
}

/**
 * The calendar's month caption ("noviembre de 2026"), through the shared formatter.
 *
 * The caption is the one calendar string with **no width constraint**, so it can come
 * from the app's formatter while the weekday headers stay on the names bundle — which
 * they must, since only the bundle has a two-letter width (`do`, not `dom`). The
 * bundle's own caption pattern is `LLLL y`, a bare month-space-year that omits the `de`
 * Spanish joins them with, so taking it from there reads as an anglicism.
 *
 * **The date is rebuilt at UTC before formatting, and that is load-bearing here.**
 * react-day-picker hands a caption formatter a *local* first-of-month, while the shared
 * formatter reads at UTC (→ invariants.md#ssr-safe-rendered-dates) — passing it straight
 * through captions a November grid `octubre de 2026` for every visitor east of UTC.
 * Taking the local Y/M and rebuilding at UTC is the pairing that rule prescribes, and
 * makes the caption name the month it was given in every zone.
 */
export function formatCalendarCaption(month: Date, locale: string): string {
    const atUtc = new Date(Date.UTC(month.getFullYear(), month.getMonth(), 1))
    return localeFormatter(locale).dateTime(atUtc, { month: 'long', year: 'numeric' })
}
